import React, { useMemo, useState } from 'react';
import { Row, Col, DatePicker, Select, Space, Alert, Button, Statistic } from 'antd';
import { DownloadOutlined, GlobalOutlined, ThunderboltOutlined, FieldTimeOutlined, ProjectOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/plots';
import { Dayjs } from 'dayjs';
import { LoadingIndicator, ErrorAlert, StorageProjectMap, ExportDialog } from '../components';
import CommonCard from '../components/CommonCard';
import { useStorageProjects, useStorageStatistics } from '../hooks/useStorageData';
import type { StorageProject } from '../types';
import './Storage.scss';
const { RangePicker } = DatePicker;
const { Option } = Select;
/**
 * 储能项目页面
 */
const Storage: React.FC = () => {
  // 筛选条件
  const [country, setCountry] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [exportDialogVisible, setExportDialogVisible] = useState(false);
  // 构建查询参数
  const queryParams = useMemo(() => {
    const params: any = {};
    if (country) params.country = country;
    if (status) params.status = status;
    if (dateRange) {
      params.start_date = dateRange[0].format('YYYY-MM-DD');
      params.end_date = dateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [country, status, dateRange]);
  // 获取数据
  const { data: projectsData, isLoading, error, refetch } = useStorageProjects(queryParams);
  const { data: statsData } = useStorageStatistics({ group_by: 'country' });
  // 【修复点】增加安全检查，确保 projects 永远是数组
  const projects = (Array.isArray(projectsData) ? projectsData : []) as StorageProject[];
  // 计算统计数据
  const statistics = useMemo(() => {
    if (!projects || projects.length === 0) {
      return {
        totalProjects: 0,
        totalCapacity: 0,
        totalPower: 0,
        avgDuration: 0,
      };
    }
    const totalCapacity = projects.reduce((sum: number, p: StorageProject) => sum + (p.capacity_mwh || 0), 0);
    const totalPower = projects.reduce((sum: number, p: StorageProject) => sum + (p.power_mw || 0), 0);
    const avgDuration = projects.reduce((sum: number, p: StorageProject) => sum + (p.duration_hours || 0), 0) / projects.length;
    return {
      totalProjects: projects.length,
      totalCapacity: Math.round(totalCapacity),
      totalPower: Math.round(totalPower),
      avgDuration: avgDuration.toFixed(1),
    };
  }, [projects]);
  // 获取唯一的国家列表
  const countries = useMemo(() => {
    if (!projects) return [];
    try {
        const countrySet = new Set(projects.map((p: StorageProject) => p.country).filter(Boolean));
        return Array.from(countrySet).sort();
    } catch (e) {
        return [];
    }
  }, [projects]);
  // 装机容量排名数据（按国家）
  const capacityRankingData = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    const countryCapacity: Record<string, number> = {};
    projects.forEach((p: StorageProject) => {
      if (p.country) {
          countryCapacity[p.country] = (countryCapacity[p.country] || 0) + (p.capacity_mwh || 0);
      }
    });
    return Object.entries(countryCapacity)
      .map(([country, capacity]) => ({
        country,
        capacity: Math.round(capacity),
      }))
      .sort((a, b) => b.capacity - a.capacity)
      .slice(0, 10); // 前10名
  }, [projects]);
  // 时间序列增长趋势数据（累计）
  const growthTrendData = useMemo(() => {
    if (!projects || projects.length === 0) return [];
    
    const yearAdded: Record<string, number> = {};
    const yearCount: Record<string, number> = {};
    projects.forEach(p => {
      let dateStr = p.commissioning_date;
      if (!dateStr && p.name) {
        const yearMatch = p.name.match(/20\d{2}/);
        if (yearMatch) {
          dateStr = `${yearMatch[0]}-01-01`; 
        }
      }
      if (dateStr) {
        const year = new Date(dateStr).getFullYear().toString();
        if (!isNaN(Number(year)) && Number(year) > 2000 && Number(year) < 2030) {
          yearAdded[year] = (yearAdded[year] || 0) + (p.capacity_mwh || 0);
          yearCount[year] = (yearCount[year] || 0) + 1;
        }
      }
    });
    const sortedYears = Object.keys(yearAdded).sort((a, b) => Number(a) - Number(b));
    let accumulated = 0;
    return sortedYears.map(year => {
      accumulated += yearAdded[year];
      return {
        year,
        capacity: Math.round(accumulated),
        added: Math.round(yearAdded[year]),
        count: yearCount[year]
      };
    });
  }, [projects]);
  // 装机容量排名图表配置
  const capacityRankingConfig = {
    data: capacityRankingData,
    xField: 'country',
    yField: 'capacity',
    label: {
      position: 'top' as const,
      style: { fill: '#666' },
    },
    xAxis: {
      label: { autoRotate: false, style: { fill: '#666' } },
      grid: { line: { style: { stroke: '#eee' } } }
    },
    yAxis: {
      label: { style: { fill: '#666' } },
      title: { text: '装机容量 (MWh)', style: { fill: '#666' } },
      grid: { line: { style: { stroke: '#eee' } } }
    },
    tooltip: {
      domStyles: {
        'g2-tooltip': {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: '#333',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #eee',
          borderRadius: '8px',
          backdropFilter: 'blur(4px)',
        }
      }
    },
    color: '#1890ff',
    meta: {
      country: { alias: '国家' },
      capacity: { alias: '装机容量' },
    },
  };
  // 时间序列增长趋势图表配置
  const growthTrendConfig = {
    data: growthTrendData,
    xField: 'year',
    yField: 'capacity',
    label: {
      position: 'top' as const,
      style: { fill: '#666' },
    },
    xAxis: {
      label: { autoRotate: false, style: { fill: '#666' } },
      grid: { line: { style: { stroke: '#eee' } } }
    },
    yAxis: {
      label: { style: { fill: '#666' } },
      title: { text: '累计装机容量 (MWh)', style: { fill: '#666' } },
      grid: { line: { style: { stroke: '#eee' } } }
    },
    tooltip: {
      domStyles: {
        'g2-tooltip': {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          color: '#333',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          border: '1px solid #eee',
          borderRadius: '8px',
          backdropFilter: 'blur(4px)',
        }
      }
    },
    color: '#52c41a',
    meta: {
      year: { alias: '年份' },
      capacity: { alias: '装机容量' },
    },
  };
  if (isLoading) {
    return <LoadingIndicator loading={true} tip="加载储能项目数据..." />;
  }
  if (error) {
    return (
      <ErrorAlert
        message="加载失败"
        description="无法加载储能项目数据，请稍后重试"
        onRetry={refetch}
      />
    );
  }
  const storageExportFields = [
    { value: 'project_name', label: '项目名称' },
    { value: 'country', label: '国家/地区' },
    { value: 'city', label: '城市' },
    { value: 'power_mw', label: '功率（MW）' },
    { value: 'capacity_mwh', label: '容量（MWh）' },
    { value: 'duration_hours', label: '持续时间（小时）' },
    { value: 'commissioning_date', label: '投运时间' },
    { value: 'status', label: '项目状态' },
    { value: 'owner', label: '业主' },
    { value: 'technology_provider', label: '技术提供商' },
  ];
  return (
    <div className="storage-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1f1f1f' }}>全球钒液流电池储能项目</h1>
          <p style={{ margin: '4px 0 0', color: '#8c8c8c' }}>展示全球钒液流电池储能项目的分布、装机容量和发展趋势</p>
        </div>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => setExportDialogVisible(true)}
          style={{ height: 40, padding: '0 24px', borderRadius: 6 }}
        >
          导出数据
        </Button>
      </div>
      <CommonCard className="filter-section" style={{ marginBottom: 24 }}>
        <Space size="large" wrap align="center" style={{ width: '100%' }}>
          <Space>
            <span style={{ fontWeight: 500 }}>国家/地区：</span>
            <Select
              style={{ width: 200 }}
              placeholder="全部国家"
              allowClear
              value={country}
              onChange={setCountry}
              size="large"
            >
              {countries.map(c => (
                <Option key={String(c)} value={String(c)}>{c}</Option>
              ))}
            </Select>
          </Space>
          <Space>
            <span style={{ fontWeight: 500 }}>项目状态：</span>
            <Select
              style={{ width: 150 }}
              placeholder="全部状态"
              allowClear
              value={status}
              onChange={setStatus}
              size="large"
            >
              <Option value="operational">运营中</Option>
              <Option value="under_construction">建设中</Option>
              <Option value="planned">规划中</Option>
            </Select>
          </Space>
          <Space>
            <span style={{ fontWeight: 500 }}>投运时间：</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as any)}
              format="YYYY-MM-DD"
              size="large"
            />
          </Space>
        </Space>
      </CommonCard>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <CommonCard style={{ textAlign: 'center' }} noPadding>
            <div style={{ padding: '24px' }}>
              <Statistic
                title="项目总数"
                value={statistics.totalProjects}
                prefix={<ProjectOutlined style={{ color: '#1890ff' }} />}
                suffix={<span style={{ fontSize: 14, color: '#999' }}>个</span>}
                valueStyle={{ fontWeight: 600 }}
              />
            </div>
          </CommonCard>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <CommonCard style={{ textAlign: 'center' }} noPadding>
            <div style={{ padding: '24px' }}>
              <Statistic
                title="总装机容量"
                value={statistics.totalCapacity}
                prefix={<ThunderboltOutlined style={{ color: '#52c41a' }} />}
                suffix={<span style={{ fontSize: 14, color: '#999' }}>MWh</span>}
                valueStyle={{ fontWeight: 600 }}
              />
            </div>
          </CommonCard>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <CommonCard style={{ textAlign: 'center' }} noPadding>
            <div style={{ padding: '24px' }}>
              <Statistic
                title="总功率"
                value={statistics.totalPower}
                prefix={<GlobalOutlined style={{ color: '#faad14' }} />}
                suffix={<span style={{ fontSize: 14, color: '#999' }}>MW</span>}
                valueStyle={{ fontWeight: 600 }}
              />
            </div>
          </CommonCard>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <CommonCard style={{ textAlign: 'center' }} noPadding>
            <div style={{ padding: '24px' }}>
              <Statistic
                title="平均持续时间"
                value={statistics.avgDuration}
                prefix={<FieldTimeOutlined style={{ color: '#eb2f96' }} />}
                suffix={<span style={{ fontSize: 14, color: '#999' }}>小时</span>}
                valueStyle={{ fontWeight: 600 }}
              />
            </div>
          </CommonCard>
        </Col>
      </Row>
      <CommonCard
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>全球项目分布地图</span>
            <span style={{ fontSize: '13px', color: '#999', fontWeight: 'normal' }}>
              点击标记查看项目详情 | 滚轮缩放 | 拖拽平移
            </span>
          </div>
        }
        style={{ marginBottom: 24 }}
        noPadding
      >
        <div style={{ height: 600 }}>
          {projects.length > 0 ? (
            <StorageProjectMap projects={projects} />
          ) : (
            <div style={{ padding: 24 }}>
              <Alert
                message="暂无数据"
                description="当前筛选条件下没有找到储能项目"
                type="info"
                showIcon
              />
            </div>
          )}
        </div>
      </CommonCard>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <CommonCard title="装机容量排名（前10）" style={{ height: '100%' }}>
            {capacityRankingData.length > 0 ? (
              <Column {...capacityRankingConfig} height={350} />
            ) : (
              <Alert message="暂无数据" type="info" showIcon />
            )}
          </CommonCard>
        </Col>
        <Col xs={24} lg={12}>
          <CommonCard title="时间序列增长趋势" style={{ height: '100%' }}>
            {growthTrendData.length > 0 ? (
              <Column {...growthTrendConfig} height={350} />
            ) : (
              <Alert message="暂无数据" type="info" showIcon />
            )}
          </CommonCard>
        </Col>
      </Row>
      <CommonCard title="图例说明" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div className="legend-item" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="legend-dot" style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#52c41a', marginRight: 8 }} />
            <span>运营中（Operational）</span>
          </div>
          <div className="legend-item" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="legend-dot" style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#faad14', marginRight: 8 }} />
            <span>建设中（Under Construction）</span>
          </div>
          <div className="legend-item" style={{ display: 'flex', alignItems: 'center' }}>
            <div className="legend-dot" style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#1890ff', marginRight: 8 }} />
            <span>规划中（Planned）</span>
          </div>
          <div className="legend-note" style={{ color: '#999', fontSize: 13, marginLeft: 'auto' }}>
            * 标记大小表示项目装机容量（MWh）
          </div>
        </div>
      </CommonCard>
      <ExportDialog
        visible={exportDialogVisible}
        onClose={() => setExportDialogVisible(false)}
        dataType="storage"
        availableFields={storageExportFields}
        currentFilters={{
          country,
          status,
          start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
          end_date: dateRange?.[1]?.format('YYYY-MM-DD'),
        }}
      />
    </div>
  );
};
export default Storage;
